import { Button, Col, Input, InputGroup, Label, Row } from "reactstrap";
import Select from "../../Common/AppSelect";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCompanyVehicle,
  createCompanyVehicleCheck,
  createCompanyVehicleSuccess,
  getCompanyVehicleDetails,
  listCompanyVehicles,
  listCompanyVehicleCheckType,
  listCompanyVehicleStatus,
  listCompanyVehicleType,
  updateCompanyVehicle,
  updateCompanyVehicleSuccess,
} from "../../../store/companyVehicle/action";
import moment from "moment";
import { AppDatePicker } from "../../Common/AppDateTimePicker";
import { handleDateFormat } from "../../Common/HandleDateTime";
import { toast } from "react-toastify";
import AppModal from "../../Common/AppModal";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { dvlaLookupCompanyVehicleApi } from "../../../helpers/fakebackend_helper";

// MinistryId must be exactly 7 alphanumeric characters - API rejects otherwise
const MINISTRY_ID_REGEX = /^[a-zA-Z0-9]{7}$/;

// Change #100671: onCreated(newVehicleId, savedData) lets a picker (e.g. the
// GPSLive assign popup) select the vehicle it just created; initialRegistration
// prefills the plate from what the user typed into that picker.
// Change #100672: duplicateFrom (a vehicle Id) opens the create form
// prefilled from that vehicle - the "style" (make, model, type, tyres...) -
// with its per-vehicle identifiers (reg plate, chassis, ministry id, mileage)
// left blank.
const CreateEditCompanyVehicle = ({
  modal,
  toggle,
  isEdit,
  onCreated,
  initialRegistration,
  duplicateFrom,
}) => {
  const InitialData = {
    RegistrationPlate: initialRegistration || "",
    Chassis: "",
    MinistryId: "",
    TyreInformation: "",
    CompanyVehicleStatus_Id: "",
    CompanyVehicleType_Id: "",
    Title: null,
    Description: null,
    Notes: "",
    MileageValue: "",
    MileageValueDateTime: "",
    Manufacturer: "",
    Model: "",
    ModelYear: "",
  };
  const [createEditData, setCreateEditData] = useState(InitialData);
  // On create only: initial compliance due dates keyed by check type Id ->
  // { DueDate, DueMileage }. Turned into CompanyVehicleCheck rows once the
  // vehicle is created and we know its Id.
  const [checkDueData, setCheckDueData] = useState({});
  const dispatch = useDispatch();
  const {
    companyVehicleDetailsAPI,
    companyVehicleStatusListAPI,
    companyVehicleTypeListAPI,
    companyVehicleCheckTypeListAPI,
    companyVehicleCreateAPI,
    companyVehicleUpdateAPI,
    companyVehicleCreateLoading,
    companyVehicleUpdateLoading,
  } = useSelector((state) => ({
    companyVehicleDetailsAPI:
      state.CompanyVehicle.companyVehicleDetails?.companyvehicle_data,
    companyVehicleStatusListAPI:
      state.CompanyVehicle.companyVehicleStatusList?.companyvehiclestatus_list,
    companyVehicleTypeListAPI:
      state.CompanyVehicle.companyVehicleTypeList?.companyvehicletype_list,
    companyVehicleCheckTypeListAPI:
      state.CompanyVehicle.companyVehicleCheckTypeList
        ?.companyvehiclechecktype_list,
    companyVehicleCreateAPI: state.CompanyVehicle.companyVehicleCreate,
    companyVehicleUpdateAPI: state.CompanyVehicle.companyVehicleUpdate,
    companyVehicleCreateLoading:
      state.CompanyVehicle.companyVehicleCreateLoading,
    companyVehicleUpdateLoading:
      state.CompanyVehicle.companyVehicleUpdateLoading,
  }));

  // Active check types (MOT, Road Tax, Service...) sorted by Order, offered
  // as optional due-date rows on the create screen.
  const activeCheckTypes = (companyVehicleCheckTypeListAPI || [])
    .filter((item) => item?.FlagActive === "1" || item?.FlagActive === 1)
    .sort((a, b) => a?.Order - b?.Order);

  useEffect(() => {
    dispatch(listCompanyVehicleStatus());
    dispatch(listCompanyVehicleType());
    dispatch(listCompanyVehicleCheckType());
    if (!isEdit) {
      setCreateEditData(InitialData);
      setCheckDueData({});
    }
    if (!isEdit && duplicateFrom) {
      dispatch(getCompanyVehicleDetails({ id: duplicateFrom }));
    }
  }, []);

  // The store may still hold another vehicle's details, so only copy once the
  // source vehicle's own record has loaded.
  useEffect(() => {
    if (isEdit || !duplicateFrom) return;
    const src = companyVehicleDetailsAPI;
    if (!src || String(src.Id) !== String(duplicateFrom)) return;
    setCreateEditData({
      ...InitialData,
      Manufacturer: src.Manufacturer || "",
      Model: src.Model || "",
      ModelYear: src.ModelYear || "",
      TyreInformation: src.TyreInformation || "",
      CompanyVehicleStatus_Id: src.CompanyVehicleStatus_Id || "",
      CompanyVehicleType_Id: src.CompanyVehicleType_Id || "",
      Title: src.Title || null,
      Description: src.Description || null,
      Notes: src.Notes || "",
    });
  }, [companyVehicleDetailsAPI]);

  // Change #100720 - DVLA lookup. Fills make, year and, on create, the MOT and
  // Road Tax compliance due dates. DVLA returns no model, so Model is left as
  // typed. Only empty fields are overwritten on edit, so a lookup never wipes
  // something the user corrected by hand.
  const [dvlaLoading, setDvlaLoading] = useState(false);
  const handleDvlaLookup = () => {
    const plate = (createEditData.RegistrationPlate || "")
      .replace(/\s+/g, "")
      .toUpperCase();
    if (!plate) {
      toast.error("Enter a registration plate first");
      return;
    }
    setDvlaLoading(true);
    dvlaLookupCompanyVehicleApi(plate)
      .then((res) => {
        if (res?.status !== 200) {
          toast.error(res?.message || "DVLA lookup failed");
          return;
        }
        const prefill = res?.companyvehicle_prefill || {};
        const pick = (current, incoming) =>
          isEdit && current ? current : incoming || current || "";
        setCreateEditData((prev) => ({
          ...prev,
          RegistrationPlate: prefill.RegistrationPlate || prev.RegistrationPlate,
          Manufacturer: pick(prev.Manufacturer, prefill.Manufacturer),
          ModelYear: pick(prev.ModelYear, prefill.ModelYear),
        }));
        // Compliance rows exist on create only. Match the check types by name
        // as each company names its own (MOT, Road Tax, Vehicle Tax...).
        const filled = [];
        if (!isEdit) {
          const findType = (re) =>
            activeCheckTypes.find((type) => re.test(type?.Name || ""));
          const motType = prefill.MotExpiryDate && findType(/\bmot\b/i);
          const taxType = prefill.TaxDueDate && findType(/tax/i);
          setCheckDueData((prev) => {
            const next = { ...prev };
            if (motType) {
              next[motType.Id] = {
                ...next[motType.Id],
                DueDate: prefill.MotExpiryDate,
              };
            }
            if (taxType) {
              next[taxType.Id] = {
                ...next[taxType.Id],
                DueDate: prefill.TaxDueDate,
              };
            }
            return next;
          });
          if (motType) filled.push(motType.Name);
          if (taxType) filled.push(taxType.Name);
        }
        const dvla = res?.dvla_data || {};
        const extras = [
          dvla.colour && "Colour: " + dvla.colour,
          dvla.fuelType && "Fuel: " + dvla.fuelType,
          prefill.MotExpiryDate &&
            "MOT expiry: " + moment(prefill.MotExpiryDate).format("DD/MM/YYYY"),
          prefill.TaxDueDate &&
            "Tax due: " + moment(prefill.TaxDueDate).format("DD/MM/YYYY"),
        ].filter(Boolean);
        toast.success(
          "Found at DVLA" +
            (filled.length ? " - " + filled.join(" and ") + " date set" : "") +
            (extras.length ? ". " + extras.join(", ") : "")
        );
      })
      .catch(() => toast.error("DVLA lookup failed - please try again later"))
      .finally(() => setDvlaLoading(false));
  };

  const handleCreateUpdate = (updateCreate) => {
    if (
      createEditData.MinistryId &&
      !MINISTRY_ID_REGEX.test(createEditData.MinistryId)
    ) {
      toast.error(
        "Ministry Id must be exactly 7 letters/numbers (e.g. C123456)"
      );
      return;
    }
    if (updateCreate === "create") {
      dispatch(createCompanyVehicle([createEditData]));
    }
    if (updateCreate === "update") {
      dispatch(updateCompanyVehicle([createEditData]));
    }
  };

  useEffect(() => {
    if (isEdit) {
      setCreateEditData({
        Id: companyVehicleDetailsAPI?.Id,
        RegistrationPlate: companyVehicleDetailsAPI?.RegistrationPlate,
        Chassis: companyVehicleDetailsAPI?.Chassis,
        MinistryId: companyVehicleDetailsAPI?.MinistryId,
        CompanyVehicleStatus_Id:
          companyVehicleDetailsAPI?.CompanyVehicleStatus_Id,
        TyreInformation: companyVehicleDetailsAPI?.TyreInformation,
        CompanyVehicleType_Id: companyVehicleDetailsAPI?.CompanyVehicleType_Id,
        Title: companyVehicleDetailsAPI?.Title,
        Description: companyVehicleDetailsAPI?.Description,
        Notes: companyVehicleDetailsAPI?.Notes,
        MileageValue: companyVehicleDetailsAPI?.MileageValue,
        MileageValueDateTime: companyVehicleDetailsAPI?.MileageValueDateTime,
        Manufacturer: companyVehicleDetailsAPI?.Manufacturer,
        Model: companyVehicleDetailsAPI?.Model,
        ModelYear: companyVehicleDetailsAPI?.ModelYear,
      });
    }
  }, [companyVehicleDetailsAPI]);

  useEffect(() => {
    if (isEmpty(companyVehicleCreateAPI)) return;
    if (companyVehicleCreateAPI.status === 200) {
      // Vehicle saved - now create any initial compliance checks the user
      // set due dates for on the create screen, against the new vehicle Id.
      const newVehicleId = companyVehicleCreateAPI?.CompanyVehicle_Id;
      if (newVehicleId) {
        const checkRows = Object.entries(checkDueData)
          .filter(([, val]) => val?.DueDate || val?.DueMileage)
          .map(([checkTypeId, val]) => ({
            CompanyVehicle_Id: newVehicleId,
            CompanyVehicleCheckType_Id: checkTypeId,
            DueDate: val?.DueDate || null,
            DueMileage: val?.DueMileage || null,
          }));
        if (checkRows.length) {
          dispatch(createCompanyVehicleCheck(checkRows));
        }
      }
      dispatch(listCompanyVehicles());
      toast.success(companyVehicleCreateAPI?.message);
      if (onCreated) onCreated(newVehicleId, createEditData);
      toggle();
    } else {
      toast.error(companyVehicleCreateAPI?.message);
    }
    dispatch(createCompanyVehicleSuccess([]));
  }, [companyVehicleCreateAPI]);

  useEffect(() => {
    if (isEmpty(companyVehicleUpdateAPI)) return;
    if (companyVehicleUpdateAPI.status === 200) {
      dispatch(listCompanyVehicles());
      dispatch(getCompanyVehicleDetails({ id: companyVehicleDetailsAPI?.Id }));
      toast.success(companyVehicleUpdateAPI?.message);
      toggle();
    } else {
      toast.error(companyVehicleUpdateAPI?.message);
    }
    dispatch(updateCompanyVehicleSuccess([]));
  }, [companyVehicleUpdateAPI]);
  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={
        isEdit
          ? "Edit Company Vehicle"
          : duplicateFrom
          ? "Duplicate Company Vehicle"
          : "Create Company Vehicle"
      }
      icon={<LocalShippingOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={companyVehicleCreateLoading || companyVehicleUpdateLoading}
      loadingText="Saving…"
      onSubmit={() => handleCreateUpdate(isEdit ? "update" : "create")}
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
    >
      <Row>
        {!isEdit && duplicateFrom && (
          <Col lg={12}>
            <small className="text-muted">
              Copied from an existing vehicle. Enter the new vehicle's
              registration, chassis, ministry id and mileage.
            </small>
          </Col>
        )}
        <Col lg={12} className={"mt-2"}>
          <Label>Registration Plate</Label>
          <InputGroup>
            <Input
              className={"form-control"}
              placeholder={"Registration Plate"}
              type="text"
              value={createEditData.RegistrationPlate}
              onChange={(e) =>
                setCreateEditData({
                  ...createEditData,
                  RegistrationPlate: e.target.value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleDvlaLookup();
                }
              }}
            />
            <Button
              color="primary"
              outline
              type="button"
              disabled={dvlaLoading || !createEditData.RegistrationPlate}
              onClick={handleDvlaLookup}
              title="Look up make, year, MOT and tax at the DVLA"
            >
              {dvlaLoading ? "Looking up…" : "DVLA Lookup"}
            </Button>
          </InputGroup>
        </Col>
        <Col lg={12}>
          <Label>Manufacturer</Label>
          <Input
            className={"form-control"}
            placeholder={"Manufacturer"}
            type="text"
            value={createEditData.Manufacturer}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Manufacturer: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12}>
          <Label>Model</Label>
          <Input
            className={"form-control"}
            placeholder={"Model"}
            type="text"
            value={createEditData.Model}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Model: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12}>
          <Label>Model Year</Label>
          <Input
            className={"form-control"}
            placeholder={"Model Year"}
            type="text"
            value={createEditData.ModelYear}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                ModelYear: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Chassis</Label>
          <Input
            className={"form-control"}
            placeholder={"Chassis"}
            type="text"
            value={createEditData.Chassis}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Chassis: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Tyre Information</Label>
          <Input
            className={"form-control"}
            placeholder={"e.g. 235/65R 16C"}
            type="text"
            value={createEditData.TyreInformation || ""}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                TyreInformation: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Ministry</Label>
          <Input
            className={"form-control"}
            placeholder={"e.g. C123456"}
            type="text"
            maxLength={7}
            invalid={
              !isEmpty(createEditData.MinistryId) &&
              !MINISTRY_ID_REGEX.test(createEditData.MinistryId)
            }
            value={createEditData.MinistryId}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                MinistryId: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Vehicle Status</Label>
          <Select
            className={"w-100"}
            options={
              !isEmpty(companyVehicleStatusListAPI)
                ? companyVehicleStatusListAPI.map((item) => ({
                    label: item?.Name,
                    value: item?.Id,
                  }))
                : []
            }
            value={
              createEditData.CompanyVehicleStatus_Id
                ? [
                    {
                      label: companyVehicleStatusListAPI?.find(
                        (item) =>
                          item.Id === createEditData.CompanyVehicleStatus_Id
                      )?.Name,
                      value: createEditData.CompanyVehicleStatus_Id,
                    },
                  ]
                : []
            }
            onChange={(e) => {
              setCreateEditData({
                ...createEditData,
                CompanyVehicleStatus_Id: e?.value,
              });
            }}
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Vehicle Type</Label>
          <Select
            className={"w-100"}
            options={
              !isEmpty(companyVehicleTypeListAPI)
                ? companyVehicleTypeListAPI.map((item) => ({
                    label: item?.Name,
                    value: item?.Id,
                  }))
                : []
            }
            value={
              createEditData.CompanyVehicleType_Id
                ? {
                    label: companyVehicleTypeListAPI?.find(
                      (item) => item.Id === createEditData.CompanyVehicleType_Id
                    )?.Name,
                    value: createEditData.CompanyVehicleType_Id,
                  }
                : []
            }
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                CompanyVehicleType_Id: e?.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Title</Label>
          <Input
            className={"form-control"}
            placeholder={"Title"}
            type="text"
            value={createEditData.Title}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Title: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Description</Label>
          <Input
            className={"form-control"}
            placeholder={"Description"}
            type="textarea"
            row={2}
            value={createEditData.Description}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Description: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Notes</Label>
          <Input
            className={"form-control"}
            placeholder={"Notes"}
            type="textarea"
            row={2}
            value={createEditData.Notes}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                Notes: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Mileage Value</Label>
          <Input
            className={"form-control"}
            placeholder={"Mileage Value"}
            type="text"
            value={createEditData.MileageValue}
            onChange={(e) =>
              setCreateEditData({
                ...createEditData,
                MileageValue: e.target.value,
              })
            }
          />
        </Col>
        <Col lg={12} className={"mt-2"}>
          <Label>Mileage Value Date Time</Label>
          <AppDatePicker
            value={
              createEditData.MileageValueDateTime
                ? moment(createEditData.MileageValueDateTime)
                : null
            }
            onChange={(val) => {
              const jsDate = val && val.isValid() ? val.toDate() : null;
              setCreateEditData({
                ...createEditData,
                MileageValueDateTime: jsDate ? handleDateFormat(jsDate) : "",
              });
            }}
          />
        </Col>
        {!isEdit && !isEmpty(activeCheckTypes) && (
          <Col lg={12} className={"mt-3"}>
            <div className="fw-semibold">Compliance Due Dates</div>
            <small className="text-muted d-block mb-2">
              Optional. Set the due date (e.g. MOT, Road Tax) to create the
              check now. Leave blank to skip - you can add checks later on the
              vehicle.
            </small>
            {activeCheckTypes.map((type) => {
              const typeIsMileageBased =
                type?.FlagMileageBased === "1" || type?.FlagMileageBased === 1;
              const row = checkDueData[type?.Id] || {};
              return (
                <div key={type?.Id} className={"mt-2"}>
                  <Label>{type?.Name}</Label>
                  <AppDatePicker
                    value={row.DueDate ? moment(row.DueDate) : null}
                    onChange={(val) => {
                      const jsDate = val && val.isValid() ? val.toDate() : null;
                      setCheckDueData((prev) => ({
                        ...prev,
                        [type?.Id]: {
                          ...prev[type?.Id],
                          DueDate: jsDate ? handleDateFormat(jsDate) : "",
                        },
                      }));
                    }}
                  />
                  {typeIsMileageBased && (
                    <Input
                      className={"form-control mt-1"}
                      placeholder={"Due Mileage"}
                      type="number"
                      value={row.DueMileage || ""}
                      onChange={(e) =>
                        setCheckDueData((prev) => ({
                          ...prev,
                          [type?.Id]: {
                            ...prev[type?.Id],
                            DueMileage: e.target.value,
                          },
                        }))
                      }
                    />
                  )}
                </div>
              );
            })}
          </Col>
        )}
      </Row>
    </AppModal>
  );
};

export default CreateEditCompanyVehicle;
